<template>
    <div class="Table" :class="{ 'Table--self-contained': selfContained }">
        <div v-if="$slots.toolbar || $slots.toolbarEnd" class="Table__toolbar">
            <div class="Table__toolbarContent">
                <slot name="toolbar" />
            </div>
            <div class="Table__toolbarEndContent">
                <slot name="toolbarEnd" />
            </div>
        </div>
        <div class="Table__container">
            <table class="Table__table">
                <thead>
                    <tr>
                        <slot name="head" />
                    </tr>
                </thead>
                <!--
                    The `body` slot replaces the default <tbody> wrapper entirely.
                    Use it when the consumer needs to own the <tbody> element directly —
                    e.g. drag-to-reorder via vuedraggable (tag="tbody"), virtual scrolling,
                    or grouped tbody sections. Falls back to the standard <tbody> + default
                    slot for all existing usages.
                -->
                <slot v-if="$slots.body" name="body" />
                <tbody v-else>
                    <slot />
                </tbody>
            </table>
            <div v-if="$slots.message" class="Table__message">
                <slot name="message" />
            </div>
            <div v-if="$slots.footer" class="Table__footer">
                <slot name="footer" />
            </div>
            <div v-if="$slots.status" class="Table__status">
                <slot name="status" />
            </div>
        </div>
    </div>
</template>

<script lang="ts">
    import throttle from 'lodash/throttle';
    import { Component, Prop, Vue } from 'vue-property-decorator';

    const SCROLL_END_THRESHOLD = 95;

    // PRODUCT-26110 Task 13 follow-up: declares its own `$emit('scroll-end')` call below
    // -- without a declared `emits`, Vue 3 warns "Component emitted event 'scroll-end'
    // but it is neither declared in the emits option nor as an 'onScrollEnd' prop" every
    // time it fires (seen x22 in live QA, sibling of the earlier `scroll` finding).
    @Component({
        emits: [ 'scroll-end' ],
    })
    export default class Table extends Vue {
        // Makes table self-contained with its own scroll container. Disabled by default for backward
        // compatibility as some existing implementations rely on page scroll.
        @Prop({ type: Boolean, default: false })
        private selfContained!: boolean;

        private container: HTMLElement | null = null;
        private scrollListener = throttle(this.handleScroll, 100);

        private mounted(): void {
            this.container = this.$el.querySelector('.Table__container');
            if (this.container) {
                this.container.addEventListener('scroll', this.scrollListener);
            }
        }

        private beforeUnmount(): void {
            if (this.container) {
                this.container.removeEventListener('scroll', this.scrollListener);
            }
            this.scrollListener.cancel();
        }

        private handleScroll(): void {
            if (!this.container) {
                return;
            }

            const {
                scrollTop,
                scrollHeight,
                clientHeight,
            } = this.container;

            const scrollPercentage = ((scrollTop + clientHeight) / scrollHeight) * 100;
            if (scrollPercentage >= SCROLL_END_THRESHOLD) {
                this.$emit('scroll-end');
            }
        }
    }
</script>
